import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ShopifyIntegrationApiPollState as ShopifyIntegrationApiPollStateType } from '@/types';
import { apiService } from '@/utils/api';

const ShopifyIntegrationApiPollState: React.FC = () => {
  const [formData, setFormData] = useState({
    last_product_poll: '',
    last_inventory_poll: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const queryClient = useQueryClient();
  const { data: sites = [] } = useQuery('sites', apiService.getSites);
  const defaultSiteId = sites.length > 0 ? sites[0].id : undefined;

  // Fetch Shopify integration timestamps
  const { data: timestampsData, isLoading } = useQuery<ShopifyIntegrationApiPollStateType>(
    'shopifyApiPollState',
    apiService.getShopifyIntegrationApiPollState,
    {
      onSuccess: (data: ShopifyIntegrationApiPollStateType) => {
        setFormData({
          last_product_poll: data.last_product_poll ?
            new Date(data.last_product_poll).toISOString().slice(0, 16) : '',
          last_inventory_poll: data.last_inventory_poll ?
            new Date(data.last_inventory_poll).toISOString().slice(0, 16) : ''
        });
      },
      onError: () => {
        // Reset form if no data found
        setFormData({
          last_product_poll: '',
          last_inventory_poll: '',
        });
      }
    }
  );

  // Create/Update mutation
  const saveMutation = useMutation(
    (data: ShopifyIntegrationApiPollStateType) => {
      if (timestampsData?.id) {
        return apiService.updateShopifyIntegrationApiPollState(data);
      } else {
        return apiService.createShopifyIntegrationApiPollState(data);
      }
    },
    {
      onSuccess: () => {
        setShowSuccess(true);
        queryClient.invalidateQueries('shopifyApiPollState');
        setTimeout(() => setShowSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteShopifyIntegrationApiPollState(),
    {
      onSuccess: () => {
        setShowDeleteSuccess(true);
        queryClient.invalidateQueries('shopifyApiPollState');
        setFormData({
          last_product_poll: '',
          last_inventory_poll: ''
        });
        setTimeout(() => setShowDeleteSuccess(false), 3000);
      },
      onError: () => {
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    }
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const siteId = timestampsData?.site_id ?? defaultSiteId;
    if (!siteId) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    const submitData: ShopifyIntegrationApiPollStateType = {
      site_id: siteId,
      ...formData,
      last_product_poll: formData.last_product_poll ?
        new Date(formData.last_product_poll).toISOString() : undefined,
      last_inventory_poll: formData.last_inventory_poll ?
        new Date(formData.last_inventory_poll).toISOString() : undefined
    };
    saveMutation.mutate(submitData);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these timestamps?')) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Loading...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopify Integration Timestamps</h1>

      {/* Success Messages */}
      {showSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Shopify Integration Timestamps saved successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {showDeleteSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Shopify Integration Timestamps deleted successfully
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {showError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Something went wrong. Please try again.
              </h3>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Product Create Latest Poll Time */}
          <div>
            <label htmlFor="product_create_latest_poll_time" className="block text-sm font-medium text-gray-700 mb-2">
              Latest time product sync
            </label>
            <input
              type="datetime-local"
              id="last_product_poll"
              name="last_product_poll"
              value={formData.last_product_poll}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Product Update Latest Poll Time */}
          <div>
            <label htmlFor="product_update_latest_poll_time" className="block text-sm font-medium text-gray-700 mb-2">
              Latest time inventory sync
            </label>
            <input
              type="datetime-local"
              id="last_inventory_poll"
              name="last_inventory_poll"
              value={formData.last_inventory_poll}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end pt-6 space-x-3">
            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save Shopify Integration Timestamps'}
            </button>

            {timestampsData?.id && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShopifyIntegrationApiPollState;
